/**
 * Device Liquidation Information Column Configuration
 *
 * Shows single row for service (device liquidation info).
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createOptionsFormatter,
    OptionItem,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { Service } from '@/modules/Service/types'
import { SERVICE_STATUS } from '@/modules/Service/constants'
import ServiceContract from '../../shared/ServiceContract'

// Row type for Device Liquidation table
export type DeviceLiquidationRow = Service

// ============================================================================
// Status Options
// ============================================================================

const getStatusOptions = (t: TFunction): OptionItem[] =>
    SERVICE_STATUS(t).map((item) => ({
        label: item.value === 'active' ? t('sold') : item.label,
        value: item.value,
        color: item.color as OptionItem['color'],
    }))

// ============================================================================
// Column Configuration
// ============================================================================

export const createDeviceLiquidationColumns = (
    t: TFunction
): DataTableColumn<DeviceLiquidationRow>[] => [
    {
        key: 'employee_names',
        title: t('employee_sale_tracking_ids'),
        width: 250,
        pinned: 'left',
        render: (value) => value?.join(', ') || '-',
    },
    {
        key: 'service_price_register',
        title: t('service_price_register'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'service_status',
        title: t('status'),
        width: 150,
        formatter: createOptionsFormatter({
            options: getStatusOptions(t),
        }),
    },
    {
        key: 'service_start_date',
        title: t('device_liquidation.service_start_date'),
        width: 110,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_contract',
        title: t('service_contract'),
        width: 300,
        render: (_, row) => {
            if (!row?.service_id) return null
            return <ServiceContract serviceID={row.service_id} />
        },
    },
]
